const PRIORITY_COLORS = {
  urgent: '#dc2626',
  high: '#f59e0b',
  medium: '#4f46e5',
  low: '#94a3b8',
};

/** Parse YYYY-MM-DD as local midnight (avoids UTC shift). */
export function parseDateOnly(str) {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function formatDateOnly(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function allDayRange(startStr, endStr) {
  const start = parseDateOnly(startStr);
  let end = parseDateOnly(endStr || startStr);
  if (!start) return null;
  if (end < start) end = new Date(start);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

export function tasksToEvents(tasks) {
  return (tasks || [])
    .filter((t) => t.due_date || t.start_date)
    .map((t) => {
      const range = allDayRange(t.start_date || t.due_date, t.due_date || t.start_date);
      if (!range) return null;
      return {
        Id: `task-${t.id}`,
        Subject: t.title,
        StartTime: range.start,
        EndTime: range.end,
        IsAllDay: true,
        CategoryColor: PRIORITY_COLORS[t.priority] || '#4f46e5',
        EventType: 'task',
        RawId: t.id,
        Status: t.status,
        Priority: t.priority,
        ProjectName: t.project_name || ([t.fund_client_name, t.fund_name].filter(Boolean).join(' · ') || null),
        AssigneeName: t.assignee_name,
        Link: '/tasks',
      };
    })
    .filter(Boolean);
}

export function calendarPayloadToEvents(data, { showTasks = true, showContracts = true, showSal = true } = {}) {
  const events = [];
  if (showTasks && data?.tasks) events.push(...tasksToEvents(data.tasks));
  if (showContracts && data?.contracts) {
    data.contracts.forEach((c) => {
      const range = allDayRange(c.expiry_date, c.expiry_date);
      if (!range) return;
      events.push({
        Id: `contract-${c.id}`,
        Subject: c.title,
        StartTime: range.start,
        EndTime: range.end,
        IsAllDay: true,
        CategoryColor: '#dc2626',
        EventType: 'contract',
        RawId: c.id,
        Status: c.status,
        ProjectName: c.project_name,
        Link: '/contracts',
      });
    });
  }
  if (showSal && data?.sal_milestones) {
    data.sal_milestones.forEach((s) => {
      const range = allDayRange(s.planned_date, s.planned_date);
      if (!range) return;
      events.push({
        Id: `sal-${s.id}`,
        Subject: `SAL ${s.seq}: ${s.title || s.client_name}`,
        StartTime: range.start,
        EndTime: range.end,
        IsAllDay: true,
        CategoryColor: '#22c55e',
        EventType: 'sal',
        RawId: s.id,
        Status: s.status,
        ClientName: s.client_name,
        FundName: s.fund_name,
        FundClientId: s.fund_client_id,
        PlannedAmount: s.planned_amount,
        Link: `/fund-clients/${s.fund_client_id}`,
      });
    });
  }
  return events;
}

/** Flatten dated tasks for the native timeline view. */
export function tasksToTimeline(tasks) {
  return (tasks || [])
    .filter((t) => t.due_date || t.start_date)
    .map((t) => {
      const start = parseDateOnly(t.start_date || t.due_date);
      const end = parseDateOnly(t.due_date || t.start_date);
      if (!start || !end) return null;
      const fundLabel = [t.fund_client_name, t.fund_name].filter(Boolean).join(' · ');
      return {
        id: t.id,
        title: t.title,
        projectId: t.fund_client_id || t.project_id || 0,
        projectName: t.project_name || fundLabel || 'Unassigned',
        start,
        end,
        status: t.status,
        priority: t.priority,
        progress: t.status === 'done' ? 100 : t.status === 'in_progress' ? 50 : t.status === 'review' ? 75 : 0,
        raw: t,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.start - b.start || a.title.localeCompare(b.title));
}

export function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

export function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

export function daysInMonthGrid(monthDate) {
  const first = startOfMonth(monthDate);
  const startPad = first.getDay(); // 0 Sun
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - startPad);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const day = new Date(gridStart);
    day.setDate(gridStart.getDate() + i);
    cells.push(day);
  }
  return cells;
}

export function eventOverlapsDay(ev, day) {
  const t0 = new Date(day); t0.setHours(0, 0, 0, 0);
  const t1 = new Date(day); t1.setHours(23, 59, 59, 999);
  return ev.StartTime <= t1 && ev.EndTime >= t0;
}
